import { requestIdleCallback } from "@theme/utilities";
/*
 * Declarative shadow DOM is only initialized on the initial render of the page.
 * If the component is mounted after the browser finishes the initial render,
 * the shadow root needs to be manually hydrated.
 */
export class DeclarativeShadowElement extends HTMLElement {
  connectedCallback() {
    if (!this.shadowRoot) {
      const template = this.querySelector(':scope > template[shadowrootmode="open"]');

      if (!(template instanceof HTMLTemplateElement)) return;

      const shadow = this.attachShadow({ mode: "open" });
      shadow.append(template.content.cloneNode(true));
    }
  }
}

/**
 * A custom ResizeObserver that only calls the callback when the element is resized.
 * By default the ResizeObserver callback is called when the element is first observed.
 */
export class ResizeNotifier extends ResizeObserver {
  #initialized = false;

  /**
   * @param {ResizeObserverCallback} callback
   */
  constructor(callback) {
    super((entries) => {
      if (this.#initialized) return callback(entries, this);
      this.#initialized = true;
    });
  }

  disconnect() {
    this.#initialized = false;
    super.disconnect();
  }
}

(() => {
  let lastWidth = null;

  function setScrollbarWidth() {
    requestIdleCallback(() => {
      // Measure the PLATFORM scrollbar width with a forced-scroll probe rather
      // than the current gutter. --scrollbar-width compensates padding when a
      // drawer sets overflow:hidden, so it must hold the width the scrollbar
      // *would* take even on a page that is currently short enough not to have
      // one — otherwise the page jumps sideways when a drawer opens.
      const outer = document.createElement("div");
      outer.style.cssText = "visibility:hidden;overflow:scroll;position:absolute;width:100px;height:100px;";
      document.body.appendChild(outer);

      const inner = document.createElement("div");
      inner.style.width = "100%";
      outer.appendChild(inner);

      const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
      const windowWidth = window.innerWidth;
      const documentWidth = document.documentElement.clientWidth;

      document.body.removeChild(outer);

      const finalWidth = scrollbarWidth > 0 ? scrollbarWidth : Math.max(0, windowWidth - documentWidth);

      // Writing a custom property on <html> invalidates inherited custom
      // properties across the whole tree, measured at 13-17ms on the next
      // layout read. The value almost never changes, so skip the write.
      if (finalWidth === lastWidth) return;
      lastWidth = finalWidth;

      document.documentElement.style.setProperty("--scrollbar-width", `${finalWidth}px`);
    });
  }

  // Defer until page is fully loaded to avoid blocking render
  if (document.readyState === "complete") {
    setScrollbarWidth();
  } else {
    window.addEventListener("load", setScrollbarWidth, { once: true });
  }

  // Recalculate on resize/orientation change. Mobile browsers fire resize
  // constantly as the URL bar shows and hides; that changes height only, so
  // key the guard on clientWidth — unlike window.innerWidth it also moves when
  // a scrollbar appears or disappears on a fixed-width viewport.
  let resizeTimeout;
  let lastClientWidth = document.documentElement.clientWidth;
  const debouncedSetScrollbarWidth = () => {
    if (document.documentElement.clientWidth === lastClientWidth) return;
    lastClientWidth = document.documentElement.clientWidth;
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(setScrollbarWidth, 100);
  };

  const forceSetScrollbarWidth = () => {
    lastClientWidth = document.documentElement.clientWidth;
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(setScrollbarWidth, 100);
  };

  window.addEventListener("resize", debouncedSetScrollbarWidth);
  window.addEventListener("orientationchange", forceSetScrollbarWidth);
})();
